import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Customer } from '@minutes-system/backend/src/api/customers/schema';
import { customersQueryAtom, buildQueryKey } from '../../../../states/customers';
import calculateMaxPage from '../../../../lib/calculateMaxPage';
import { CustomerSearchCondition } from '../types';

interface UseCustomersInterface {
  page: number;
  setPage: (page: number) => void;
  customers: Customer[];
  total: number;
  pageSize: number;
  searchValues: CustomerSearchCondition;
  handleSearch: (values: CustomerSearchCondition) => void;
  handleClear: () => void;
}

// backend の 1ページ件数 (service.ts の PER_PAGE と揃える)
const PAGE_SIZE = 10;

const useCustomers = (): UseCustomersInterface => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const page = Number.isInteger(pageParam) && pageParam >= 1 ? pageParam : 1;

  const searchValues: CustomerSearchCondition = {
    name: searchParams.get('name') ?? undefined,
    phone: searchParams.get('phone') ?? undefined,
    mobilePhone: searchParams.get('mobile-phone') ?? undefined,
  };

  const queryKey = buildQueryKey({ page, ...searchValues });
  const data = useAtomValue(customersQueryAtom(queryKey));

  // pageパラメータが無効、または最大ページ数を超えている場合は1ページ目にリダイレクト
  const maxPage = calculateMaxPage(data.count, PAGE_SIZE);
  const isInvalidPage = !Number.isInteger(pageParam) || pageParam < 1 || pageParam > maxPage;
  useEffect(() => {
    if (isInvalidPage) {
      const params = new URLSearchParams(searchParams);
      params.set('page', '1');
      setSearchParams(params, { replace: true });
    }
  }, [isInvalidPage, searchParams, setSearchParams]);

  const setPage = useCallback(
    (next: number) => {
      if (next < 1) {
        return;
      }
      const params = new URLSearchParams(searchParams);
      params.set('page', String(next));
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(
    (values: CustomerSearchCondition) => {
      const params = new URLSearchParams();
      if (values.name) {
        params.set('name', values.name);
      }
      if (values.phone) {
        params.set('phone', values.phone);
      }
      if (values.mobilePhone) {
        params.set('mobile-phone', values.mobilePhone);
      }
      params.set('page', '1');
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleClear = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  return {
    page,
    setPage,
    customers: data.customers,
    total: data.count,
    pageSize: PAGE_SIZE,
    searchValues,
    handleSearch,
    handleClear,
  };
};

export default useCustomers;
